import { Fragment, useEffect, useMemo, useState } from "react";
import { Account, api, Manifest, Order, Position } from "../api";
import { fmtTime } from "../format";
import { px, signedUsd, usd } from "../lib/money";
import { RECENT_BUSINESS_DAYS, splitRecent } from "../lib/recent";
import OlderToggle from "./OlderToggle";
import Ticker from "./Ticker";
import TradeStory from "./TradeStory";

export default function Blotter({ manifest, book }: { manifest: Manifest; book: string }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [account, setAccount] = useState<Account | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [storyId, setStoryId] = useState<string | null>(null);
  // Display-only: the blotter keeps every order, but shows this week first.
  const [showOlder, setShowOlder] = useState(false);
  const shown = useMemo(() => {
    const { recent, older } = splitRecent(orders, (o) => o.submitted_at);
    return { all: orders, recent, older };
  }, [orders]);

  useEffect(() => {
    if (book === "all") return; // no single real account to show — see message below
    void load();
    const iv = setInterval(load, (manifest.frontend.refresh_seconds || 15) * 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book]);

  async function load() {
    try {
      setOrders(await api.get<Order[]>(`/api/trading/orders?book=${book}`));
      setPositions(await api.get<Position[]>(`/api/trading/positions?book=${book}`));
      setAccount(await api.get<Account>(`/api/trading/account?book=${book}`));
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }

  if (book === "all") {
    return (
      <div className="card text-slate-500 text-sm font-sans">
        Orders are per-account — pick a specific book above to view its blotter.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Execution Blotter</h1>
        {account && (
          <span className={`badge ${account.env === "paper" ? "bg-gold/20 text-gold" : "bg-down/20 text-down"}`}>
            {account.env.toUpperCase()} ACCOUNT
          </span>
        )}
      </div>
      {error && <div className="text-down text-sm">{error}</div>}

      <div className="card">
        <h2 className="label">Open positions</h2>
        {/* Desktop table — clipped invisible on phones under the frame's
            overflow-x-hidden until Kaizen D3/F4; mobile gets Holdings-style
            cards instead. */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase">
                <th className="py-1">Symbol</th><th>Qty</th><th>Avg entry</th><th>Mkt value</th><th>Unrealized</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {positions.map((p) => (
                <tr key={p.symbol} className="border-t border-edge">
                  <td className="py-1.5 font-bold"><Ticker symbol={p.symbol} /></td>
                  <td>{p.qty}</td>
                  <td>{px(p.symbol, p.avg_entry_price)}</td>
                  <td>{usd(p.market_value)}</td>
                  <td className={p.unrealized_pl >= 0 ? "text-up" : "text-down"}>
                    {signedUsd(p.unrealized_pl)}
                  </td>
                </tr>
              ))}
              {positions.length === 0 && (
                <tr><td colSpan={5} className="py-2 text-slate-600 font-sans">flat — no open positions</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="sm:hidden space-y-2">
          {positions.map((p) => (
            <div key={p.symbol} className="rounded-lg border border-edge p-2.5">
              <div className="flex items-center gap-2">
                <Ticker symbol={p.symbol} className="font-mono font-bold" />
                <span className="text-xs text-slate-400">×{p.qty}</span>
                <span className={`ml-auto font-mono font-bold ${p.unrealized_pl >= 0 ? "text-up" : "text-down"}`}>
                  {signedUsd(p.unrealized_pl)}
                </span>
              </div>
              <div className="text-xs font-mono text-slate-400 mt-1">
                entry {px(p.symbol, p.avg_entry_price)}
                <span className="text-slate-600"> · value {usd(p.market_value)}</span>
              </div>
            </div>
          ))}
          {positions.length === 0 && (
            <div className="text-slate-600 text-sm font-sans py-1">flat — no open positions</div>
          )}
        </div>
      </div>

      <div className="card overflow-x-auto">
        <h2 className="label">Orders</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase">
              <th className="py-1">Time</th><th>Symbol</th><th>Side</th><th>Qty</th>
              <th>Type</th><th>Purpose</th><th>Status</th><th>Filled</th><th>Avg px</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(showOlder ? shown.all : shown.recent).map((o) => {
              const dead = o.status === "error" || o.status === "rejected" || o.status === "canceled";
              return (
              <Fragment key={o.id}>
              <tr className={`border-t border-edge ${dead ? (o.error_reason ? "" : "opacity-50") : ""}`}>
                <td className="py-1.5 text-slate-500">{fmtTime(o.submitted_at)}</td>
                <td className={`font-bold ${dead ? "line-through" : ""}`}><Ticker symbol={o.symbol} /></td>
                <td className={dead ? "text-slate-500 line-through" : o.side === "buy" ? "text-up" : "text-down"}>
                  {/* Both directions get annotated, not just one. A SHORT is
                      closed by a BUY, so scanning the side column for "sell"
                      to find the closes silently misses every short's exit —
                      it reads as if the position was never closed (Alan,
                      2026-08-04: "why are there no sells? these appear in the
                      performance tab as already closed out"). "sell (short)"
                      was already labelled; "buy (cover)" was not. */}
                  {o.side}
                  {o.side === "sell" && o.purpose === "entry" ? " (short)" : ""}
                  {o.side === "buy" && (o.purpose === "exit" || o.purpose === "flatten") ? " (cover)" : ""}
                </td>
                <td className={dead ? "line-through" : ""}>{o.qty}</td>
                <td>{o.order_type}{o.limit_price ? `@${o.limit_price}` : ""}</td>
                <td className={o.purpose === "flatten" || o.purpose === "kill" ? "text-gold" : "text-slate-400"}>{o.purpose}</td>
                <td className={dead ? "text-down" : ""}>
                  {dead ? `${o.status} · never filled` : o.status}
                </td>
                <td>{o.filled_qty}</td>
                <td>{o.filled_avg_price ? px(o.symbol, o.filled_avg_price) : "—"}</td>
              </tr>
              {dead && o.error_reason && (
                <tr className="bg-down/5">
                  <td></td>
                  <td colSpan={8} className="pb-1.5 pl-1 text-xs text-down font-sans">
                    ↳ why it failed: {o.error_reason}
                  </td>
                </tr>
              )}
              {/* The round trip, made explicit. An order alone never says
                  whether the position was closed — and a short closes with a
                  BUY, so the blotter read as if nothing was ever sold. Each
                  link names the counterpart, its realised P&L, and opens that
                  exact lot's Story. */}
              {o.links.map((l, i) => (
                <tr key={`${o.id}-l${i}`} className="bg-slate-800/20">
                  <td></td>
                  <td colSpan={8} className="pb-1.5 pl-1 text-xs font-sans text-slate-400">
                    ↳ {l.role === "closes" ? "closes" : "closed by"}{" "}
                    <span className="text-slate-300">
                      {l.qty} {l.symbol} {l.side}
                    </span>{" "}
                    {l.role === "closes"
                      ? `opened ${l.entry_time ? fmtTime(l.entry_time) : "—"}`
                      : `on ${l.exit_time ? fmtTime(l.exit_time) : "—"}`}
                    {" · "}
                    <span className={l.pnl >= 0 ? "text-up" : "text-down"}>
                      {signedUsd(l.pnl)}
                    </span>
                    {l.trade_key && (
                      <button className="btn text-[10px] ml-2"
                              onClick={() => setStoryId(l.trade_key!)}>
                        Story →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              </Fragment>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan={9} className="py-2 text-slate-600 font-sans">no orders yet</td></tr>
            )}
            {orders.length > 0 && shown.recent.length === 0 && !showOlder && (
              <tr><td colSpan={9} className="py-2 text-slate-600 font-sans">
                No orders in the last {RECENT_BUSINESS_DAYS} business days.
              </td></tr>
            )}
            {shown.older.length > 0 && (
              <tr><td colSpan={9} className="border-t border-edge">
                <OlderToggle count={shown.older.length} open={showOlder}
                             onToggle={() => setShowOlder(!showOlder)} noun="older orders" />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {storyId && <TradeStory exitOrderId={storyId} onClose={() => setStoryId(null)} />}
    </div>
  );
}
